import { Alert, Button, Checkbox, Modal, Space } from "antd";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../contexts/Notifications";
import { useUpdateProjectWorkingDaysMutation } from "../../generated/types";

export const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5];

// Postgres DOW convention: 0 = Sunday … 6 = Saturday.
const DAYS_OF_WEEK: { dow: number; label: string }[] = [
  { dow: 0, label: "Sunday" },
  { dow: 1, label: "Monday" },
  { dow: 2, label: "Tuesday" },
  { dow: 3, label: "Wednesday" },
  { dow: 4, label: "Thursday" },
  { dow: 5, label: "Friday" },
  { dow: 6, label: "Saturday" },
];

type Props = {
  customer: string;
  project: string;
  workingDays: number[] | null | undefined;
  visible: boolean;
  setVisible: (vis: boolean) => void;
};

export const EditWorkingDaysModal = (props: Props) => {
  const { customer, project, workingDays, visible, setVisible } = props;
  const notify = useNotification();

  const initialSelected = useMemo(
    () => workingDays ?? DEFAULT_WORKING_DAYS,
    [workingDays],
  );
  const [selected, setSelected] = useState<number[]>(initialSelected);

  useEffect(() => {
    if (visible) {
      setSelected(initialSelected);
    }
  }, [visible, initialSelected]);

  const [updateWorkingDays, { loading }] = useUpdateProjectWorkingDaysMutation({
    onCompleted: () => {
      notify(
        `Working days updated for ${project}. Planned progress is recomputing in the background and will update shortly.`,
        "success",
      );
      setVisible(false);
    },
    onError: (err) =>
      notify(`Failed to update working days: ${err.message}`, "error"),
  });

  const toggleDay = (dow: number) => {
    setSelected((current) =>
      current.includes(dow)
        ? current.filter((d) => d !== dow)
        : [...current, dow].sort((a, b) => a - b),
    );
  };

  const isDefault = _.isEqual(
    [...selected].sort((a, b) => a - b),
    DEFAULT_WORKING_DAYS,
  );

  const canSave = selected.length > 0 && !loading;

  const handleSave = () => {
    updateWorkingDays({
      variables: {
        input: {
          customer,
          project,
          // Send null when the user reset to default so the column goes back
          // to NULL (preserves "never configured" state across projects).
          workingDays: isDefault ? null : selected,
        },
      },
    });
  };

  const handleReset = () => {
    setSelected(DEFAULT_WORKING_DAYS);
  };

  return (
    <Modal
      title={`Working days — ${project}`}
      open={visible}
      onCancel={() => setVisible(false)}
      footer={[
        <Button key="reset" onClick={handleReset} disabled={loading}>
          Reset to default (Mon–Fri)
        </Button>,
        <Button
          key="cancel"
          onClick={() => setVisible(false)}
          disabled={loading}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          disabled={!canSave}
          loading={loading}
        >
          Save
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        message="Unchecked days are treated as non-working (no planned progress accrues on them). At least one day must be checked. Saving queues a planned-progress rebuild that runs in the background."
        style={{ marginBottom: 16 }}
      />
      <Space direction="vertical">
        {DAYS_OF_WEEK.map(({ dow, label }) => (
          <Checkbox
            key={dow}
            checked={selected.includes(dow)}
            disabled={loading}
            onChange={() => toggleDay(dow)}
          >
            {label}
          </Checkbox>
        ))}
      </Space>
      {selected.length === 0 && (
        <div style={{ color: "#cf1322", marginTop: 12 }}>
          Select at least one working day.
        </div>
      )}
    </Modal>
  );
};
